import { 
  Input,
  Directive,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  OnChanges,
  SimpleChanges,
  Renderer2,
} 
from '@angular/core';
import { 
  Validator,
  AbstractControl,
  ValidationErrors,
  NG_VALUE_ACCESSOR,
  CheckboxRequiredValidator,
} 
from '@angular/forms';

/**
 * enums
 */
import { ICheckType } from '@sharedModules/forms/components/check/enums/check-type';

/**
 * models
 */
import { FormCheckFieldModel } from '@sharedModules/forms/models/form-check-field/form-check-field-model';


/**
 * @directive FormCheckFieldDirective
 * @description
 * **Angular directive for checkbox and radio button fields** with full form integration and validation.
 *
 * This directive extends `FormCheckFieldModel` to provide a concrete implementation for checkbox
 * and radio button inputs. It implements Angular's `ControlValueAccessor` pattern for seamless
 * form integration and includes built-in validation for required fields.
 *
 * **Key Features:**
 * - 🎯 **Form Integration** - Full `ControlValueAccessor` implementation
 * - ✅ **Validation** - Built-in required field validation
 * - 🎪 **Event Handling** - Automatic change and blur event management
 * - 🏷️ **Host Bindings** - Dynamic attribute binding for all input properties
 * - 🛡️ **Type Safety** - Extends `FormCheckFieldModel` with boolean value handling
 * - 🎨 **Styling** - Automatic Bootstrap form-check-input class application
 *
 * **Common Use Cases:**
 * - Form checkboxes with validation
 * - Radio button groups
 * - Terms and conditions acceptance
 * - Feature toggles and settings
 * - Multi-select checkboxes
 *
 * @example
 * <!-- Basic checkbox -->
 * <input appFormCheckField
 *   id="accept-terms"
 *   name="acceptTerms"
 *   type="checkbox"
 *   [required]="true">
 *
 * @example
 * <!-- Radio button -->
 * <input appFormCheckField
 *   id="gender-male"
 *   name="gender"
 *   type="radio"
 *   value="male">
 */
@Directive({
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormCheckFieldDirective),
      multi: true
    },
    {
      provide: CheckboxRequiredValidator,
      useExisting: forwardRef(() => FormCheckFieldDirective),
    }
  ],
  host: {
    '[class.form-check-input]': 'true',
    '[attr.checked]': 'isChecked ? isChecked : null',
  },
  selector: '[appFormCheckField]'
})
export class FormCheckFieldDirective extends FormCheckFieldModel implements Validator, OnChanges {

  /**
   * @method onHandlerCheck
   * @description
   * **Handles change events** from the checkbox or radio button input.
   *
   * This method is automatically called when the user interacts with the input element.
   * It delegates to the base model's `onInputChange` method to handle the value update
   * and form integration.
   *
   * @param {Event} event - The change event from the input element
   * @example
   * // Called automatically when user checks/unchecks the input
   * <input appFormCheckField (change)="onHandlerCheck($event)">
   */
  @HostListener('change', ['$event'])
  public onHandlerCheck(event: Event): void {
    this.onInputChange(event)
  }

  /**
   * @method onHandlerTouched
   * @description
   * **Handles blur events** from the checkbox or radio button input.
   *
   * This method is automatically called when the input loses focus. It delegates to
   * the base model's `onInputTouched` method to update the touched state for
   * validation and form state management.
   *
   * @example
   * // Called automatically when input loses focus
   * <input appFormCheckField (blur)="onHandlerTouched()">
   */
  @HostListener('blur')
  public onHandlerTouched(): void {
    this.onInputTouched()
  }

  /**
   * @property type
   * @description
   * **The input type** for the checkbox or radio button.
   *
   * This property determines whether the input behaves as a checkbox or radio button.
   * It accepts `CheckType` enum values or string values and is bound to the host element's
   * type attribute for proper HTML semantics.
   *
   * @type {CheckType | string}
   * @example
   * <!-- Checkbox type -->
   * <input appFormCheckField type="checkbox" id="accept-terms" name="acceptTerms">
   *
   * @example
   * <!-- Radio button type -->
   * <input appFormCheckField type="radio" id="gender-male" name="gender" value="male">
   */
  @HostBinding('attr.type')
  @Input('type')
  public type!: ICheckType

  /**
   * @property value
   * @description
   * **The value attribute** for the checkbox or radio button input.
   *
   * This property sets the value attribute of the input element. For checkboxes, this
   * is typically used to specify what value should be submitted when the checkbox is
   * checked. For radio buttons, this determines which option is selected.
   *
   * @type {unknown}
   * @example
   * <!-- Checkbox with value -->
   * <input appFormCheckField type="checkbox" id="newsletter" name="newsletter" value="subscribe">
   *
   * @example
   * <!-- Radio button with value -->
   * <input appFormCheckField type="radio" id="gender-female" name="gender" value="female">
   */
  @HostBinding('attr.value')
  @Input('value')
  public value!: unknown

  /**
   * @property checked
   * @description
   * **The checked state** of the checkbox or radio button.
   *
   * This property controls whether the input is currently checked/selected.
   * It can be set programmatically or bound to a form control value.
   *
   * @type {boolean}
   * @default false
   * @example
   * <!-- Checked checkbox -->
   * <input appFormCheckField type="checkbox" id="remember" name="remember" [checked]="true">
   *
   * @example
   * <!-- Radio button with checked state -->
   * <input appFormCheckField type="radio" id="option1" name="options" [checked]="selectedOption === 'option1'">
   */
  @Input('checked')
  public checked: boolean = false

  /**
   * Unique identifier of the form field.
   * Used to associate the label with the input.
   */
  @HostBinding('id')
  @Input({ alias: 'id', required: true })
  public id!: string;

  /**
   * Name attribute for the input.
   */
  @HostBinding('attr.name')
  @Input({ alias: 'name', required: true })
  public name!: string;

  /**
   * Whether the input is readonly.
   */
  @HostBinding('attr.readonly')
  @Input({ alias: 'readonly' })
  public readonly: boolean = false;

  /**
   * Whether the input is disabled.
   */
  /**
   * @description disabled status
   */
  @HostBinding('attr.disabled')
  public get disabledAttribute(): string | null {
    return this.disabled ? 'disabled' : null;
  }

  @Input({ alias: "disabled"})
  public disabled: boolean = false;

  /**
   * Whether the input is required.
   */
  @Input({ alias: 'required' })
  public required: boolean = false;

  /**
   * @description Whether the checkbox is indeterminate
   * @default false
   */
  @Input({  alias: 'indeterminate' })
  public indeterminate: boolean = false;

  /**
   * @constructor
   * @description
   * **Initializes the form check field directive** with element reference and renderer.
   *
   * The constructor injects the `ElementRef<HTMLInputElement>` to provide access to the
   * underlying HTML input element and `Renderer2` for safe DOM manipulation.
   *
   * @param {ElementRef<HTMLInputElement>} inputElementRef - Reference to the HTML input element
   * @param {Renderer2} _renderer - Angular renderer for safe DOM manipulation
   * @example
   * // Automatically injected by Angular's dependency injection
   * constructor(
   *   public inputElementRef: ElementRef<HTMLInputElement>,
   *   private _renderer: Renderer2
   * ) {
   *   super();
   * }
   */
  constructor(
    private _renderer: Renderer2,
    public inputElementRef: ElementRef<HTMLInputElement>,
  ) {
    super()
  }

  /**
   * @method ngOnChanges
   * @description
   * **Handles changes to input properties**, specifically the indeterminate state.
   *
   * This lifecycle hook is called whenever any input property changes. It updates
   * the indeterminate state on the native input element when the indeterminate input changes.
   *
   * @param {SimpleChanges} changes - Object containing information about changed properties
   */
  public ngOnChanges(changes: SimpleChanges): void {
    
    if ('checked' in changes) {
      this.writeValue(this.checked)
    }

    if ('indeterminate' in changes) {
      this.updateIndeterminateState(this.indeterminate);
    }
  }

  /**
   * @method updateIndeterminateState
   * @description
   * **Updates the indeterminate property** on the native input element using Renderer2.
   *
   * This method sets the indeterminate state using Angular's Renderer2 for safe,
   * platform-agnostic DOM manipulation. The indeterminate property is a DOM property
   * (not an attribute) that can only be set programmatically.
   *
   * @private
   */
  private updateIndeterminateState(state: boolean): void {
    this._renderer.setProperty(this.inputElementRef.nativeElement, 'indeterminate', state ? 'true' : '');
  }

  /**
   * @method validate
   * @description
   * **Validates the checkbox or radio button value** according to the required constraint.
   *
   * This method implements Angular's `Validator` interface to provide custom validation
   * for checkbox and radio button inputs. It checks if the field is required and whether
   * the control has a truthy value.
   *
   * **Validation Logic:**
   * - If the field is required and the control value is falsy → returns `{ required: true }`
   * - If the field is not required or the control value is truthy → returns `null`
   *
   * @param {AbstractControl} control - The form control to validate
   * @returns {ValidationErrors | null} - Validation errors or null if valid
   * @example
   * // Used automatically by Angular forms
   * const errors = this.validate(formControl);
   * if (errors) {
   *   console.log('Validation errors:', errors);
   * }
   */
  public validate(control: AbstractControl): ValidationErrors | null {
    
    // if the control is required and the value is false, return an error 
    if (this.required && !control.value) {
      return { required: true };
    }

    // return null if the control is not required or the value is true
    return null;
    
  }


}
